/**
 * Free TV: publicly available channels seeded from the iptv-org community
 * lists (see workers/api/src/lib/freeChannels.ts). No account and no
 * credentials sit behind these, so unlike Live there is one shared library,
 * not one per Xtream login -- just a category rail and a channel list.
 */

import { useEffect, useRef, useState } from 'react';
import type { FreeCategoryDto, FreeChannelDto } from '@sflx/shared';
import { EmptyState, ErrorNotice, Spinner } from '../components/Feedback';
import { Icon } from '../components/Icon';
import { SafeImage } from '../components/SafeImage';
import { useDebounced } from '../hooks/useDebounced';
import { useVirtualList } from '../hooks/useVirtualList';
import { freeTv } from '../lib/api';
import { formatCount } from '../lib/format';
import { freeWatchLink } from '../lib/links';
import { Link, navigate } from '../router/router';

const ROW_HEIGHT = 56;
const PAGE_SIZE = 100;

type Step = 'categories' | 'channels';

export function FreeTv() {
  const [categories, setCategories] = useState<FreeCategoryDto[] | null>(null);
  const [categoriesError, setCategoriesError] = useState<unknown>(null);
  const [categorySlug, setCategorySlug] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [step, setStep] = useState<Step>('categories');

  const debouncedSearch = useDebounced(search, 250);

  useEffect(() => {
    let cancelled = false;
    freeTv
      .categories()
      .then((result) => {
        if (!cancelled) setCategories(result.categories);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setCategoriesError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Search has to work from the category rail too on mobile, where the two
  // steps are shown one at a time -- see the CatalogBrowser/Live fix this
  // mirrors.
  useEffect(() => {
    if (search.trim()) setStep('channels');
  }, [search]);

  const totalCount = (categories ?? []).reduce((sum, category) => sum + category.count, 0);
  const heading = categorySlug ? (categories?.find((c) => c.slug === categorySlug)?.label ?? 'Channels') : 'All channels';

  return (
    <div className="page page--flush">
      <div className="usa-test__bar">
        <span className="pane__title">Free TV</span>
        <Link to="/free/playlist?scope=us" className="btn btn--ghost btn--sm" style={{ marginLeft: 'auto' }}>
          Test public playlist
        </Link>
      </div>
      <div className="pane__search">
        <label className="row" style={{ gap: 'var(--s-3)', flexWrap: 'nowrap' }}>
          <Icon name="search" size={16} />
          <span className="sr-only">Search free channels</span>
          <input
            className="input"
            type="search"
            placeholder="Search free channels"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      <div className="panes" data-step={step} style={{ gridTemplateColumns: '268px minmax(0, 1fr)' }}>
        <section className="pane" data-pane="categories" aria-label="Categories">
          <div className="pane__head">
            <span className="pane__title">Categories</span>
          </div>
          <div className="pane__body">
            {categoriesError ? (
              <div style={{ padding: 'var(--s-4)' }}>
                <ErrorNotice error={categoriesError} />
              </div>
            ) : null}
            {categories === null ? (
              <div style={{ padding: 'var(--s-5)' }}>
                <Spinner label="Loading" />
              </div>
            ) : (
              <>
                <ListRow
                  selected={categorySlug === null}
                  label="All channels"
                  count={totalCount}
                  onClick={() => {
                    setCategorySlug(null);
                    setStep('channels');
                  }}
                />
                {categories.map((category) => (
                  <ListRow
                    key={category.slug}
                    selected={categorySlug === category.slug}
                    label={category.label}
                    count={category.count}
                    onClick={() => {
                      setCategorySlug(category.slug);
                      setStep('channels');
                    }}
                  />
                ))}
                {!categories.length ? (
                  <p className="muted" style={{ padding: 'var(--s-5)' }}>
                    Nothing synced yet -- check back after the next refresh.
                  </p>
                ) : null}
              </>
            )}
          </div>
        </section>

        <ChannelPane
          heading={heading}
          category={categorySlug}
          search={debouncedSearch}
          onBack={() => setStep('categories')}
        />
      </div>
    </div>
  );
}

function ListRow({
  selected,
  label,
  count,
  onClick,
}: {
  selected: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button type="button" className="list-row" aria-selected={selected} onClick={onClick}>
      <span className="list-row__label">{label}</span>
      <span className="list-row__count">{formatCount(count)}</span>
    </button>
  );
}

interface ChannelPaneProps {
  heading: string;
  category: string | null;
  search: string;
  onBack: () => void;
}

function ChannelPane({ heading, category, search, onBack }: ChannelPaneProps) {
  const [items, setItems] = useState<FreeChannelDto[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const signature = `${category ?? ''}|${search}`;

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    freeTv
      .channels({ category, q: search || null, limit: PAGE_SIZE })
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, [signature]);

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    freeTv
      .channels({ category, q: search || null, cursor, limit: PAGE_SIZE })
      .then((page) => {
        setItems((current) => [...(current ?? []), ...page.items]);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
      })
      .catch(() => {
        // Leaves what already loaded on screen; scrolling back down retries.
      })
      .finally(() => setLoadingMore(false));
  };

  const list = useVirtualList({ count: items?.length ?? 0, rowHeight: ROW_HEIGHT });
  const { end } = list;

  useEffect(() => {
    if (hasMore && !loadingMore && items && end >= items.length - 20) loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, end, items?.length]);

  const previousSignature = useRef(signature);
  useEffect(() => {
    if (previousSignature.current === signature) return;
    previousSignature.current = signature;
    list.scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const play = (channel: FreeChannelDto) => navigate(freeWatchLink(channel.id, channel.name));

  return (
    <section className="pane" data-pane="channels" aria-label="Channels">
      <div className="pane__head">
        <button
          type="button"
          className="btn btn--ghost btn--sm btn--icon"
          onClick={onBack}
          aria-label="Back to categories"
        >
          <Icon name="chevronLeft" size={15} />
        </button>
        <span className="list-row__label" style={{ fontWeight: 600 }}>
          {heading}
        </span>
      </div>

      <div className="pane__body" ref={list.ref} onScroll={list.onScroll}>
        {error ? (
          <div style={{ padding: 'var(--s-4)' }}>
            <ErrorNotice error={error} />
          </div>
        ) : null}

        {items === null ? (
          <div style={{ padding: 'var(--s-5)' }}>
            <Spinner label="Loading channels" />
          </div>
        ) : !items.length ? (
          <EmptyState
            title={search ? `Nothing matches "${search}"` : 'No channels here'}
            hint={search ? 'Try another spelling, or clear the search.' : 'Try another category.'}
          />
        ) : (
          <div className="vlist" style={{ height: list.totalHeight }}>
            <div className="vlist__window" style={{ transform: `translateY(${list.offsetY}px)` }}>
              {items.slice(list.start, list.end).map((channel) => (
                <div className="channel-row" key={channel.id}>
                  <button type="button" className="channel-row__play" onClick={() => play(channel)}>
                    <span className="channel-row__logo">
                      <SafeImage src={channel.logoUrl} />
                    </span>
                    <span className="channel-row__body">
                      <span className="channel-row__name">{channel.name}</span>
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loadingMore ? (
          <div style={{ padding: 'var(--s-4)' }}>
            <Spinner label="Loading more" />
          </div>
        ) : null}
      </div>
    </section>
  );
}
